import { FormEvent, useEffect, useState } from 'react';
import {
  AdminMember,
  AdminMemberOrders,
  AdminProposal,
  ProposalDownloadResponse,
} from '../../auth/memberApi';
import { useAuth } from '../../auth/AuthContext';
import { formatStoredUsPhoneForInput } from '../../auth/phone';
import OrderCards from '../CustomerOrders/OrderCards';
import './index.css';

const formatDate = (value: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const formatProposalDate = (proposal: AdminProposal) => (
  formatDate(proposal.UpdatedOn || proposal.CreatedOn)
);

const formatCentralEmailSentDate = (value: string) => {
  const date = value ? new Date(value) : new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const getPart = (type: string) => parts.find((part) => part.type === type)?.value || '';
  return `${getPart('month')}-${getPart('day')}- ${getPart('hour')}:${getPart('minute')}`;
};

const isDownloadExpired = (download?: ProposalDownloadResponse) => {
  if (!download?.CreatedOn) {
    return false;
  }
  return Date.now() > new Date(download.CreatedOn).getTime() + (48 * 60 * 60 * 1000);
};

const Admin = () => {
  const {
    createMember,
    createMemberProposal,
    createMemberProposalDownload,
    createAdminInvoiceDownload,
    createOrderInvoice,
    deleteMember,
    listAdminOrders,
    listMembers,
    inviteMember,
    uploadMemberProposalFiles,
    uploadOrderInvoiceFiles,
  } = useAuth();
  const [activeView, setActiveView] = useState<'members' | 'orders' | 'add-user'>('members');
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [memberOrders, setMemberOrders] = useState<AdminMemberOrders[]>([]);
  const [expandedOrderMembers, setExpandedOrderMembers] = useState<Record<string, boolean>>({});
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [invoiceBusyOrderId, setInvoiceBusyOrderId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [proposalNames, setProposalNames] = useState<Record<string, string>>({});
  const [busyAction, setBusyAction] = useState('');
  const [downloadLinks, setDownloadLinks] = useState<Record<string, ProposalDownloadResponse>>({});
  const [emailSentMessages, setEmailSentMessages] = useState<Record<string, string>>({});
  const [memberToDelete, setMemberToDelete] = useState<AdminMember | null>(null);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeletingMember, setIsDeletingMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '', email: '', companyName: '', website: '', phone: '', emailOptIn: true, isReseller: false,
  });
  const [createdMember, setCreatedMember] = useState<AdminMember | null>(null);
  const [createMemberError, setCreateMemberError] = useState('');
  const [isCreatingMember, setIsCreatingMember] = useState(false);
  const [invitingMemberId, setInvitingMemberId] = useState('');

  const handleCreateMember = async (event: FormEvent) => {
    event.preventDefault();
    setCreateMemberError('');
    setIsCreatingMember(true);
    try {
      const member = await createMember(newMember);
      setMembers((current) => [member, ...current]);
      setCreatedMember(member);
      setNewMember({
        name: '', email: '', companyName: '', website: '', phone: '', emailOptIn: true, isReseller: false,
      });
    } catch (requestError) {
      setCreateMemberError(requestError instanceof Error ? requestError.message : 'Unable to create member.');
    } finally {
      setIsCreatingMember(false);
    }
  };

  const handleInviteMember = async (member: AdminMember) => {
    setError('');
    setCreateMemberError('');
    setInvitingMemberId(member.memberId);
    try {
      const updated = await inviteMember(member.memberId);
      replaceMember(updated);
      if (createdMember?.memberId === updated.memberId) setCreatedMember(updated);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Unable to invite member.';
      if (activeView === 'add-user') setCreateMemberError(message); else setError(message);
    } finally {
      setInvitingMemberId('');
    }
  };

  useEffect(() => {
    let isCurrent = true;

    const loadMembers = async () => {
      setIsLoading(true);
      setError('');
      try {
        const nextMembers = await listMembers();
        if (isCurrent) {
          setMembers(nextMembers);
        }
      } catch (requestError) {
        if (isCurrent) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load members.');
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    loadMembers();

    return () => {
      isCurrent = false;
    };
  }, [listMembers]);

  const replaceMember = (nextMember: AdminMember) => {
    setMembers((currentMembers) => currentMembers.map((member) => (
      member.memberId === nextMember.memberId ? nextMember : member
    )));
  };

  const showOrders = async () => {
    setActiveView('orders');
    if (ordersLoaded || ordersLoading) {
      return;
    }
    setOrdersLoading(true);
    setOrdersError('');
    try {
      setMemberOrders(await listAdminOrders());
      setOrdersLoaded(true);
    } catch (requestError) {
      setOrdersError(requestError instanceof Error ? requestError.message : 'Unable to load orders.');
    } finally {
      setOrdersLoading(false);
    }
  };

  const totalOrderCount = memberOrders.reduce((total, group) => total + group.orders.length, 0);

  const toggleMemberOrders = (memberId: string) => {
    setExpandedOrderMembers((current) => ({
      ...current,
      [memberId]: !current[memberId],
    }));
  };

  const handleCreateInvoice = async (memberId: string, orderId: string) => {
    setOrdersError('');
    setInvoiceBusyOrderId(orderId);
    try {
      const invoice = await createOrderInvoice(memberId, orderId);
      setMemberOrders((groups) => groups.map((group) => (
        group.member.memberId !== memberId ? group : {
          ...group,
          orders: group.orders.map((order) => (
            order.orderId !== orderId ? order : {
              ...order,
              invoice,
            }
          )),
        }
      )));
    } catch (requestError) {
      setOrdersError(requestError instanceof Error ? requestError.message : 'Unable to create invoice.');
    } finally {
      setInvoiceBusyOrderId('');
    }
  };

  const handleUploadInvoice = async (memberId: string, orderId: string, files: File[]) => {
    setOrdersError('');
    setInvoiceBusyOrderId(orderId);
    try {
      await uploadOrderInvoiceFiles(memberId, orderId, files);
    } catch (requestError) {
      setOrdersError(requestError instanceof Error ? requestError.message : 'Unable to upload invoice files.');
    } finally {
      setInvoiceBusyOrderId('');
    }
  };

  const handleDownloadInvoice = async (memberId: string, orderId: string) => {
    setOrdersError('');
    setInvoiceBusyOrderId(orderId);
    try {
      const download = await createAdminInvoiceDownload(memberId, orderId);
      const link = document.createElement('a');
      link.href = download.downloadUrl;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.click();
    } catch (requestError) {
      setOrdersError(requestError instanceof Error ? requestError.message : 'Unable to download invoice.');
    } finally {
      setInvoiceBusyOrderId('');
    }
  };

  const handleCreateProposal = async (member: AdminMember) => {
    const proposalName = (proposalNames[member.memberId] || '').trim();
    if (!proposalName) {
      setError('Enter a proposal name before creating it.');
      return;
    }

    setError('');
    setBusyAction(`create-${member.memberId}`);
    try {
      const nextMember = await createMemberProposal(member.memberId, proposalName);
      replaceMember(nextMember);
      setProposalNames((currentNames) => ({ ...currentNames, [member.memberId]: '' }));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to create proposal.');
    } finally {
      setBusyAction('');
    }
  };

  const handleCreateDownload = async (
    member: AdminMember,
    proposal: AdminProposal,
    email = false,
  ) => {
    setError('');
    setBusyAction(`${email ? 'email' : 'download'}-${proposal.proposalId}`);
    try {
      const existingDownload = downloadLinks[proposal.proposalId];
      if (email && existingDownload && isDownloadExpired(existingDownload)) {
        setError('The secure download link has expired. Create a new link before emailing it.');
        return;
      }
      const download = await createMemberProposalDownload(
        member.memberId,
        proposal.proposalId,
        email,
        email ? existingDownload : undefined,
      );
      setDownloadLinks((currentLinks) => ({
        ...currentLinks,
        [proposal.proposalId]: download,
      }));
      if (email && download.emailSent) {
        setEmailSentMessages((currentMessages) => ({
          ...currentMessages,
          [proposal.proposalId]: `Email Sent - ${formatCentralEmailSentDate(download.CreatedOn)}`,
        }));
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to create proposal download.');
    } finally {
      setBusyAction('');
    }
  };

  const handleUploadFiles = async (
    member: AdminMember,
    proposal: AdminProposal,
    fileList: FileList | null,
  ) => {
    const files = Array.from(fileList || []);
    if (!files.length) {
      return;
    }

    setError('');
    setBusyAction(`upload-${proposal.proposalId}`);
    try {
      await uploadMemberProposalFiles(member.memberId, proposal.proposalId, files);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to upload proposal files.');
    } finally {
      setBusyAction('');
    }
  };

  const closeDeleteDialog = () => {
    if (isDeletingMember) {
      return;
    }
    setMemberToDelete(null);
    setDeleteConfirmationText('');
    setDeleteError('');
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) {
      return;
    }
    setDeleteError('');
    if (deleteConfirmationText.trim().toLowerCase() !== 'delete') {
      setDeleteError("Type 'delete' to deactivate this member.");
      return;
    }

    setIsDeletingMember(true);
    try {
      const deletedMember = await deleteMember(memberToDelete.memberId, deleteConfirmationText);
      replaceMember(deletedMember);
      setMemberToDelete(null);
      setDeleteConfirmationText('');
      setDeleteError('');
    } catch (requestError) {
      setDeleteError(requestError instanceof Error ? requestError.message : 'Unable to delete member.');
    } finally {
      setIsDeletingMember(false);
    }
  };

  return (
    <section className="admin-page">
      <div className="admin-page-header">
        <p>Admin</p>
        <h1>Hogcloud admin.</h1>
      </div>

      <nav className="admin-subnav" aria-label="Admin tools">
        <button
          className={activeView === 'members' ? 'active' : ''}
          onClick={() => setActiveView('members')}
          type="button"
        >
          Show Members
        </button>
        <button
          className={activeView === 'orders' ? 'active' : ''}
          onClick={showOrders}
          type="button"
        >
          Show orders
        </button>
        <button
          className={activeView === 'add-user' ? 'active' : ''}
          onClick={() => setActiveView('add-user')}
          type="button"
        >
          Add user
        </button>
      </nav>

      {activeView === 'add-user' && (
        <div className="admin-panel admin-add-user-panel">
          <div className="admin-panel-header">
            <h2>Add user</h2>
            <span>Create the account first, then optionally send an invitation.</span>
          </div>
          {createMemberError && <p className="admin-message error" role="alert">{createMemberError}</p>}
          <form className="admin-user-form" onSubmit={handleCreateMember}>
            <label>Name<input required value={newMember.name} onChange={(event) => setNewMember((value) => ({ ...value, name: event.target.value }))} /></label>
            <label>Email<input required type="email" value={newMember.email} onChange={(event) => setNewMember((value) => ({ ...value, email: event.target.value }))} /></label>
            <label>Company<input value={newMember.companyName} onChange={(event) => setNewMember((value) => ({ ...value, companyName: event.target.value }))} /></label>
            <label>Phone<input type="tel" value={newMember.phone} onChange={(event) => setNewMember((value) => ({ ...value, phone: event.target.value }))} /></label>
            <label className="admin-user-form-wide">Website<input type="url" placeholder="https://" value={newMember.website} onChange={(event) => setNewMember((value) => ({ ...value, website: event.target.value }))} /></label>
            <label className="admin-user-check"><input type="checkbox" checked={newMember.emailOptIn} onChange={(event) => setNewMember((value) => ({ ...value, emailOptIn: event.target.checked }))} /> Email opt-in</label>
            <label className="admin-user-check"><input type="checkbox" checked={newMember.isReseller} onChange={(event) => setNewMember((value) => ({ ...value, isReseller: event.target.checked }))} /> Reseller</label>
            <div className="admin-user-form-actions">
              <button type="submit" disabled={isCreatingMember}>{isCreatingMember ? 'Creating…' : 'Create user'}</button>
            </div>
          </form>
          {createdMember && (
            <div className="admin-created-user">
              <div><strong>{createdMember.name}</strong><span>{createdMember.email}</span></div>
              <button type="button" onClick={() => handleInviteMember(createdMember)} disabled={invitingMemberId === createdMember.memberId}>
                {invitingMemberId === createdMember.memberId ? 'Sending…' : createdMember.InvitedOn ? 'Resend invitation' : 'Invite to login'}
              </button>
              {createdMember.InvitedOn && <span>Invitation sent {formatDate(createdMember.InvitedOn)}</span>}
            </div>
          )}
        </div>
      )}

      {activeView === 'members' && (
        <div className="admin-panel">
          <div className="admin-panel-header">
            <h2>Members</h2>
            <span>{members.length} total</span>
          </div>

          {error && <p className="admin-message error" role="alert">{error}</p>}
          {isLoading && <p className="admin-message">Loading members...</p>}

          {!isLoading && !error && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Company</th>
                    <th>Phone</th>
                    <th>Reseller</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Proposals</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const proposals = member.proposals || [];
                    return (
                      <tr key={member.memberId}>
                        <td data-label="Name">{member.name || '-'}</td>
                        <td data-label="Email">{member.email || '-'}</td>
                        <td data-label="Company">{member.companyName || '-'}</td>
                        <td data-label="Phone">{formatStoredUsPhoneForInput(member.phone) || '-'}</td>
                        <td data-label="Reseller">{member.isReseller ? 'Yes' : 'No'}</td>
                        <td data-label="Status">{member.status || 'ACTIVE'}</td>
                        <td data-label="Joined">{formatDate(member.CreatedOn)}</td>
                        <td data-label="Proposals">
                          <div className="admin-proposals-cell">
                            {proposals.length > 0 ? (
                              <ul className="admin-proposal-list">
                                {proposals.map((proposal) => {
                                  const download = downloadLinks[proposal.proposalId];
                                  const emailSentMessage = emailSentMessages[proposal.proposalId];
                                  const actionKey = busyAction.endsWith(proposal.proposalId);
                                  return (
                                    <li key={proposal.proposalId}>
                                      <div className="admin-proposal-summary">
                                        <strong>{proposal.proposalName}</strong>
                                        <span>{proposal.status || 'DRAFT'} · {formatProposalDate(proposal)}</span>
                                      </div>
                                      <div className="admin-proposal-actions">
                                        <label className="admin-proposal-upload">
                                          <span>{busyAction === `upload-${proposal.proposalId}` ? 'Uploading' : 'Upload'}</span>
                                          <input
                                            type="file"
                                            multiple
                                            onChange={(event) => {
                                              handleUploadFiles(member, proposal, event.target.files);
                                              event.target.value = '';
                                            }}
                                            disabled={busyAction === `upload-${proposal.proposalId}`}
                                          />
                                        </label>
                                        <button
                                          type="button"
                                          onClick={() => handleCreateDownload(member, proposal)}
                                          disabled={actionKey}
                                        >
                                          Link
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleCreateDownload(member, proposal, true)}
                                          disabled={actionKey}
                                        >
                                          Email
                                        </button>
                                      </div>
                                      {download && (
                                        <a
                                          className="admin-proposal-download"
                                          href={download.downloadUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Secure download · 48 hours
                                        </a>
                                      )}
                                      {emailSentMessage && (
                                        <span className="admin-proposal-email-sent">
                                          {emailSentMessage}
                                        </span>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              <span className="admin-proposal-empty">No proposals</span>
                            )}
                            <div className="admin-proposal-create">
                              <input
                                aria-label={`Proposal name for ${member.email}`}
                                placeholder="Proposal name"
                                value={proposalNames[member.memberId] || ''}
                                onChange={(event) => setProposalNames((currentNames) => ({
                                  ...currentNames,
                                  [member.memberId]: event.target.value,
                                }))}
                              />
                              <button
                                type="button"
                                onClick={() => handleCreateProposal(member)}
                                disabled={busyAction === `create-${member.memberId}`}
                              >
                                Create
                              </button>
                            </div>
                          </div>
                        </td>
                        <td data-label="Actions">
                          {member.status !== 'DELETED' && (
                            <button
                              className="admin-danger-link"
                              type="button"
                              onClick={() => setMemberToDelete(member)}
                            >
                              Delete member
                            </button>
                          )}
                          <button
                            className="admin-link-button"
                            type="button"
                            onClick={() => handleInviteMember(member)}
                            disabled={invitingMemberId === member.memberId}
                          >
                            {invitingMemberId === member.memberId ? 'Sending…' : member.InvitedOn ? 'Resend invite' : 'Invite'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={9}>No members found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeView === 'orders' && (
        <div className="admin-panel admin-orders-panel">
          <div className="admin-panel-header">
            <h2>Orders by user</h2>
            <span>{totalOrderCount} total</span>
          </div>

          {ordersError && <p className="admin-message error" role="alert">{ordersError}</p>}
          {ordersLoading && <p className="admin-message">Loading orders...</p>}

          {!ordersLoading && !ordersError && ordersLoaded && memberOrders.length === 0 && (
            <div className="customer-orders-empty">
              <h2>No orders found.</h2>
              <p>Member orders will appear here after purchases are recorded.</p>
            </div>
          )}

          {!ordersLoading && !ordersError && memberOrders.map((group, index) => {
            const isExpanded = expandedOrderMembers[group.member.memberId] === true;
            const ordersId = `admin-member-orders-${index}`;
            return (
              <section className="admin-order-group" key={group.member.memberId}>
                <button
                  type="button"
                  className="admin-order-user"
                  aria-expanded={isExpanded}
                  aria-controls={ordersId}
                  onClick={() => toggleMemberOrders(group.member.memberId)}
                >
                  <div>
                    <h3>{group.member.name || group.member.email || 'Unnamed member'}</h3>
                    <span>{group.member.email || 'No email address'}</span>
                    {group.member.companyName && <span>{group.member.companyName}</span>}
                  </div>
                  <span className="admin-order-count">
                    <strong>{group.orders.length} order{group.orders.length === 1 ? '' : 's'}</strong>
                    <span aria-hidden="true" className="admin-order-chevron">⌄</span>
                  </span>
                </button>
                {isExpanded && (
                  <div id={ordersId}>
                    <OrderCards
                      orders={group.orders}
                      onCreateInvoice={(order) => handleCreateInvoice(group.member.memberId, order.orderId)}
                      onUploadInvoice={(order, files) => handleUploadInvoice(
                        group.member.memberId,
                        order.orderId,
                        files,
                      )}
                      onDownloadInvoice={(order) => handleDownloadInvoice(
                        group.member.memberId,
                        order.orderId,
                      )}
                      invoiceBusyOrderId={invoiceBusyOrderId}
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {memberToDelete && (
        <div className="admin-modal-backdrop" role="presentation" onClick={closeDeleteDialog}>
          <div
            className="admin-modal admin-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-member-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="delete-member-title">Delete member</h2>
            <p>
              This marks the member as deleted without removing their profile, orders,
              proposal files, or Cognito user. Type delete to confirm.
            </p>
            <div className="admin-delete-member-summary">
              <strong>{memberToDelete.name || memberToDelete.email}</strong>
              <span>{memberToDelete.email}</span>
            </div>
            {deleteError && <p className="admin-message error" role="alert">{deleteError}</p>}
            <div className="admin-delete-field">
              <label htmlFor="delete-member-confirmation">Confirmation</label>
              <input
                id="delete-member-confirmation"
                autoComplete="off"
                value={deleteConfirmationText}
                onChange={(event) => setDeleteConfirmationText(event.target.value)}
              />
            </div>
            <div className="admin-modal-actions">
              <button className="admin-link-button" type="button" onClick={closeDeleteDialog}>
                Keep member
              </button>
              <button
                className="admin-delete-button"
                type="button"
                onClick={handleDeleteMember}
                disabled={isDeletingMember}
              >
                {isDeletingMember ? 'Deleting...' : 'Delete member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Admin;
